import { useEffect, useMemo, useState } from 'react';
import { MdSearch, MdMergeType } from 'react-icons/md';
import Modal, { DialogBody, DialogFooter } from '../ui/Dialog';
import { Badge, Button } from '../ui';
import { merge, mergePreview } from '../../api/addressBook';
import { messaggioErrore } from '../../utils/apiError';

/**
 * Unione di due persone: prima si vede cosa succede, poi si conferma.
 * Si uniscono solo persone scelte a mano: nessuna unione automatica per nome.
 */
export default function MergeDialog({ open, onClose, target, people, onMerged }) {
  const [query, setQuery] = useState('');
  const [scelta, setScelta] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    if (!open) { setQuery(''); setScelta(null); setAnteprima(null); setErrore(''); }
  }, [open]);

  const candidati = useMemo(() => {
    if (!target) return [];
    const q = query.trim().toLocaleLowerCase();
    const numeri = new Set((target.identities || []).map(i => i.phone).filter(Boolean));
    return people
      .filter(p => p.id !== target.id)
      .map(p => ({
        ...p,
        // Stesso numero su account diversi: è il caso tipico del duplicato.
        stessoNumero: (p.identities || []).some(i => i.phone && numeri.has(i.phone)),
      }))
      .filter(p => !q || `${p.display_name} ${p.short_name || ''}`.toLocaleLowerCase().includes(q))
      .sort((a, b) => (b.stessoNumero ? 1 : 0) - (a.stessoNumero ? 1 : 0)
        || a.display_name.localeCompare(b.display_name))
      .slice(0, 60);
  }, [people, target, query]);

  const guarda = async (persona) => {
    setScelta(persona); setAnteprima(null); setErrore(''); setBusy(true);
    try {
      setAnteprima(await mergePreview({ target: target.id, sources: [persona.id] }));
    } catch (e) { setErrore(messaggioErrore(e)); } finally { setBusy(false); }
  };

  const conferma = async () => {
    setBusy(true); setErrore('');
    try {
      const esito = await merge({ target: target.id, sources: [scelta.id] });
      onMerged?.(esito);
      onClose();
    } catch (e) { setErrore(messaggioErrore(e)); } finally { setBusy(false); }
  };

  if (!target) return null;

  return (
    <Modal open={open} onClose={onClose} size="md" label={`Unisci con ${target.display_name}`}
      description="Le identità dell'altra persona passano qui. Si può annullare.">
      <DialogBody className="space-y-3">
        {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

        {!anteprima ? (
          <>
            <div className="relative">
              <MdSearch size={18} aria-hidden className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
              <input aria-label="Cerca la persona da unire" placeholder="Cerca per nome"
                value={query} onChange={e => setQuery(e.target.value)}
                className="w-full rounded border border-line bg-surface py-2 pl-8 pr-3 text-base md:text-sm" />
            </div>
            <ul className="max-h-72 overflow-auto rounded border border-line">
              {candidati.map(p => (
                <li key={p.id} className="border-b border-line last:border-0">
                  <button type="button" onClick={() => guarda(p)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{p.display_name}</span>
                    {p.stessoNumero && <Badge tone="warning">Stesso numero</Badge>}
                    <Badge>{p.identities?.length || 0} identità</Badge>
                  </button>
                </li>
              ))}
              {!candidati.length && <li className="p-3 text-sm text-muted">Nessuna persona trovata.</li>}
            </ul>
          </>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-ink">
              <strong>{anteprima.absorbed[0]?.name}</strong> confluisce in{' '}
              <strong>{anteprima.target.name}</strong>.
            </p>
            <ul className="space-y-1 text-muted">
              <li>Identità dopo l&apos;unione: <strong className="text-ink">{anteprima.identities_after}</strong></li>
              <li>Gruppi rubrica coinvolti: {anteprima.absorbed[0]?.groups ?? 0}</li>
            </ul>
            {anteprima.conflicts.length > 0 && (
              <div className="rounded bg-warning-bg p-2 text-xs text-warning">
                <p className="font-medium">Valori diversi nei due profili:</p>
                <ul className="mt-1 list-disc pl-4">
                  {anteprima.conflicts.map(c => (
                    <li key={c.field}>{c.field}: {c.values.join(' / ')}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted">{anteprima.notice}</p>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        {anteprima ? (
          <>
            <Button variant="ghost" onClick={() => { setAnteprima(null); setScelta(null); }}>Indietro</Button>
            <Button variant="primary" icon={MdMergeType} loading={busy} onClick={conferma}>Unisci</Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
        )}
      </DialogFooter>
    </Modal>
  );
}
